/**
 * Generate color shades from a base color
 * This function creates lighter and darker variants of a given color
 * to match Material-UI's color palette structure
 */

/**
 * Convert hex color to RGB
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null;
}

/**
 * Convert RGB to hex
 */
function rgbToHex(r, g, b) {
  return (
    '#' +
    [r, g, b]
      .map((x) => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
}

/**
 * Lighten a color by a percentage
 */
function lighten(hex, percent) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const { r, g, b } = rgb;
  const amount = percent / 100;

  const newR = r + (255 - r) * amount;
  const newG = g + (255 - g) * amount;
  const newB = b + (255 - b) * amount;

  return rgbToHex(newR, newG, newB);
}

/**
 * Darken a color by a percentage
 */
function darken(hex, percent) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const { r, g, b } = rgb;
  const amount = percent / 100;

  const newR = r * (1 - amount);
  const newG = g * (1 - amount);
  const newB = b * (1 - amount);

  return rgbToHex(newR, newG, newB);
}

/**
 * Generate color shades palette from a base color
 * Creates a full Material-UI compatible color palette
 *
 * @param {string} baseColor - Hex color code (e.g., '#e87722')
 * @param {string} contrastText - Text color for contrast (default: '#fff')
 * @returns {object} Color palette with shades from lighter to darker
 */
export function generateColorShades(baseColor, contrastText = '#fff') {
  return {
    lighter: lighten(baseColor, 80), // Very light variant
    100: lighten(baseColor, 70), // Light variant
    200: lighten(baseColor, 60), // Light variant
    light: lighten(baseColor, 40), // Light variant
    400: lighten(baseColor, 20), // Light variant
    main: baseColor, // Main color
    dark: darken(baseColor, 20), // Dark variant
    700: darken(baseColor, 40), // Dark variant
    darker: darken(baseColor, 60), // Darker variant
    900: darken(baseColor, 80), // Very dark variant
    contrastText // Text color for contrast
  };
}

/**
 * Determine if a color is light or dark
 * Used to determine the appropriate contrast text color
 */
export function isColorLight(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return true;

  // Calculate luminance
  const { r, g, b } = rgb;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5;
}

/**
 * Get appropriate contrast text color for a background color
 */
export function getContrastText(backgroundColor) {
  return isColorLight(backgroundColor) ? '#000' : '#fff';
}
